import { z } from 'zod';
import { cashSessionIdSchema, customerIdSchema, orderIdSchema, orderItemIdSchema, productIdSchema, tenantIdSchema, userIdSchema, } from '../ids/index.js';
import { cents } from '../money/index.js';
export const orderOrigin = {
    table: 'table',
    delivery: 'delivery',
    counter: 'counter',
};
export const orderOriginSchema = z.enum(['table', 'delivery', 'counter']);
export const orderStatus = {
    draft: 'draft',
    placed: 'placed',
    preparing: 'preparing',
    ready: 'ready',
    delivered: 'delivered',
    paid: 'paid',
    cancelled: 'cancelled',
};
export const orderStatusSchema = z.enum([
    'draft',
    'placed',
    'preparing',
    'ready',
    'delivered',
    'paid',
    'cancelled',
]);
export const orderItemSchema = z.object({
    id: orderItemIdSchema,
    tenantId: tenantIdSchema,
    orderId: orderIdSchema,
    productId: productIdSchema,
    qty: z.number().int().min(1),
    unitPriceCents: cents,
    /** Array JSON de modifiers aplicados: `[{ id, name, priceDeltaCents }]`. */
    modifiersJson: z.string().default('[]'),
    note: z.string().max(500).nullable().default(null),
    createdAt: z.number().int(),
    updatedAt: z.number().int(),
});
export const deliveryAddressSchema = z.object({
    line1: z.string().min(1).max(200),
    line2: z.string().max(200).nullable().default(null),
    neighborhood: z.string().max(100).nullable().default(null),
    city: z.string().min(1).max(100),
    municipalityCode: z.string().max(10).nullable().default(null),
    phone: z.string().max(30).nullable().default(null),
    reference: z.string().max(500).nullable().default(null),
});
export const orderSchema = z.object({
    id: orderIdSchema,
    tenantId: tenantIdSchema,
    origin: orderOriginSchema,
    tableNumber: z.number().int().positive().nullable().default(null),
    deliveryAddressJson: z.string().nullable().default(null), // serialized DeliveryAddress
    customerId: customerIdSchema.nullable().default(null),
    openedByUserId: userIdSchema,
    status: orderStatusSchema.default('draft'),
    subtotalCents: cents,
    taxCents: cents,
    tipCents: cents.default(0),
    discountCents: cents.default(0),
    totalCents: cents,
    cashSessionId: cashSessionIdSchema.nullable().default(null),
    note: z.string().max(1000).nullable().default(null),
    openedAt: z.number().int(),
    closedAt: z.number().int().nullable().default(null),
    version: z.number().int().min(1).default(1),
    createdAt: z.number().int(),
    updatedAt: z.number().int(),
    deletedAt: z.number().int().nullable().default(null),
});
/**
 * FSM completa — la del preset restobar. La usamos tal cual cuando la feature
 * `kitchen_flow` está activa. Sin kitchen_flow la caja de cocina se colapsa
 * (ver `allowedTransitionsWithout`).
 */
const allowedTransitions = {
    draft: ['placed', 'cancelled'],
    placed: ['preparing', 'cancelled'],
    preparing: ['ready', 'cancelled'],
    ready: ['delivered', 'cancelled'],
    delivered: ['paid'],
    paid: [],
    cancelled: [],
};
/**
 * FSM sin flujo de cocina. Retail/ferretería: el pedido va directo de
 * confirmado a pagado sin pasar por preparando/listo/entregado.
 */
const allowedTransitionsNoKitchen = {
    draft: ['placed', 'cancelled'],
    placed: ['paid', 'cancelled'],
    preparing: ['paid', 'cancelled'], // quedó huérfano: permitir salida
    ready: ['paid', 'cancelled'],
    delivered: ['paid'],
    paid: [],
    cancelled: [],
};
export const canTransition = (from, to) => allowedTransitions[from].includes(to);
/**
 * Variante feature-aware. Pasa `kitchenFlow: false` para los tenants sin el
 * módulo de cocina (preset retail). El dominio sigue siendo uno solo; solo
 * cambia el mapa de transiciones aceptadas.
 */
export const canTransitionWithFeatures = (from, to, features) => {
    const table = features.kitchenFlow ? allowedTransitions : allowedTransitionsNoKitchen;
    return table[from].includes(to);
};
/**
 * Próximo estado canónico (el botón "Confirmar" / "Marcar en preparación" /
 * "Marcar pagado" que la UI ofrece). Depende del feature set: sin
 * `kitchenFlow`, `placed` avanza directo a `paid`.
 */
export const nextStatus = (from, features) => {
    if (features.kitchenFlow) {
        switch (from) {
            case 'draft':
                return 'placed';
            case 'placed':
                return 'preparing';
            case 'preparing':
                return 'ready';
            case 'ready':
                return 'delivered';
            case 'delivered':
                return 'paid';
            default:
                return null;
        }
    }
    switch (from) {
        case 'draft':
            return 'placed';
        case 'placed':
            return 'paid';
        default:
            return null;
    }
};
//# sourceMappingURL=order.js.map